import type { Locale } from "@borealis/layout-express";
import type { I18n } from "@lingui/core";
import { useLingui } from "@lingui/react";
import { formatNumber } from "@mobility-item-page/utilities";
import { useAppConfig } from "src/context/AppConfigProviderContext.tsx";
import FieldValuePair from "../FieldValuePair.tsx";
import { EvExpandableRow } from "./EvExpandableRow.tsx";

/**
 * Battery capacity is shown in two places on the range and battery card and CSS picks one:
 * beside the WLTP range from `lg` and up, as an expandable row below that.
 */
const getBatteryCapacity = (batteryCapacity: number, i18n: I18n, locale: Locale) => ({
  title: i18n.t({ id: "itemPageLayout.keyInfo.battery_capacity" }),
  value: i18n.t({
    id: "itemPageLayout.keyInfo.battery_capacity.postfix",
    values: { capacity: formatNumber(batteryCapacity, locale) },
  }),
  description: i18n.t({
    id: "itemPageLayout.ev.rangeAndBattery.batteryCapacity.body",
    message:
      "Battery capacity tells you how much energy the battery can store. Electric cars have a 'gross' (total) and 'net' (usable) capacity. The car only uses the net capacity to protect battery health. Higher capacity usually means longer range, though actual capacity gradually decreases with age and mileage.",
    comment: "Body text of the battery capacity expandable on electric car ads",
  }),
});

type Props = {
  batteryCapacity: number | undefined;
};

/** Beside the WLTP range in the spec grid, from `lg` and up. Explainer lives in its tooltip. */
export const DesktopBatteryCapacity = ({ batteryCapacity }: Props) => {
  const { i18n } = useLingui();
  const { locale } = useAppConfig();

  if (!batteryCapacity) {
    return null;
  }

  const { title, value, description } = getBatteryCapacity(batteryCapacity, i18n, locale);

  return (
    <FieldValuePair
      className="hidden lg:block"
      field={title}
      value={value}
      tooltip={description}
      tooltipPlacement="bottom"
    />
  );
};

/** The first expandable row below `lg`, where there is no room for a second spec column. */
export const MobileBatteryCapacity = ({ batteryCapacity }: Props) => {
  const { i18n } = useLingui();
  const { locale } = useAppConfig();

  if (!batteryCapacity) {
    return null;
  }

  const { title, value, description } = getBatteryCapacity(batteryCapacity, i18n, locale);

  return <EvExpandableRow className="lg:hidden" title={title} value={value} body={description} />;
};
